"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";

export default function RegistroPage() {
  const router = useRouter();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      setError("Las contraseñas no coinciden");
      return;
    }
    setLoading(true);
    setError(null);

    const res = await fetch("/api/auth/registro", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, email, password }),
    });

    const json = await res.json();
    setLoading(false);

    if (!res.ok) {
      setError(json.error || "Error al crear la cuenta");
      return;
    }

    router.push("/nueva-demanda");
    router.refresh();
  }

  const strength = password.length === 0 ? 0 : password.length < 6 ? 1 : password.length < 10 ? 2 : 3;
  const strengthLabel = ["", "Débil", "Aceptable", "Segura"][strength];
  const strengthColor = ["", "bg-red-500", "bg-amber-500", "bg-emerald-500"][strength];

  return (
    <div className="min-h-screen bg-zinc-950 flex">
      {/* Panel izquierdo */}
      <div className="hidden lg:flex lg:w-[45%] relative flex-col items-center justify-center overflow-hidden bg-zinc-900 border-r border-zinc-800">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg,#ffffff 0,#ffffff 1px,transparent 0,transparent 60px),repeating-linear-gradient(90deg,#ffffff 0,#ffffff 1px,transparent 0,transparent 60px)",
          }}
        />
        <div className="absolute left-0 inset-y-0 w-px bg-gradient-to-b from-transparent via-amber-500/40 to-transparent" />

        <div className="relative z-10 flex flex-col items-center text-center px-16 max-w-md">
          <ScalesIcon />
          <div className="mt-10 mb-3">
            <h1 className="text-5xl font-black tracking-[0.2em] text-white uppercase">SPARTA</h1>
            <h2 className="text-xl font-light tracking-[0.55em] text-amber-500 uppercase mt-1">LEGAL</h2>
          </div>
          <div className="w-12 h-px bg-amber-500/50 my-8" />
          <p className="text-zinc-400 text-sm leading-relaxed font-light">
            Únase a la plataforma de gestión jurídica utilizada por profesionales del derecho de confianza.
          </p>
          <div className="mt-16 bg-zinc-800/50 border border-zinc-700/50 rounded-xl p-6 text-left w-full">
            <p className="text-zinc-300 text-xs font-semibold uppercase tracking-widest mb-4">Incluye acceso a</p>
            {[
              "Gestión completa de expedientes",
              "Plantillas de demanda con IA",
              "Extracción automática de datos",
              "Generación de documentos Word y PDF",
            ].map((f) => (
              <div key={f} className="flex items-center gap-3 py-2 border-b border-zinc-700/30 last:border-0">
                <CheckIcon />
                <span className="text-zinc-400 text-sm">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Panel derecho — formulario */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-[400px]">
          {/* Logo móvil */}
          <div className="flex flex-col items-center mb-10 lg:hidden">
            <ScalesIcon size="sm" />
            <h1 className="mt-5 text-3xl font-black tracking-[0.2em] text-white uppercase">SPARTA</h1>
            <p className="text-amber-500 tracking-[0.5em] text-sm uppercase font-light mt-1">LEGAL</p>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-white tracking-tight">Crear cuenta</h2>
            <p className="text-zinc-500 text-sm mt-1.5">Complete el formulario para registrarse</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-xs font-semibold text-zinc-400 uppercase tracking-widest mb-2">
                Nombre completo
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                required
                placeholder="Ej: Luis García Martínez"
                className="w-full bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-700 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-amber-500/60 focus:ring-1 focus:ring-amber-500/30 transition-all"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-xs font-semibold text-zinc-400 uppercase tracking-widest mb-2">
                Correo electrónico
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                placeholder="nombre@despacho.es"
                className="w-full bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-700 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-amber-500/60 focus:ring-1 focus:ring-amber-500/30 transition-all"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-xs font-semibold text-zinc-400 uppercase tracking-widest mb-2">
                Contraseña
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                  placeholder="Mínimo 6 caracteres"
                  className="w-full bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-700 rounded-lg px-4 py-3 text-sm pr-11 focus:outline-none focus:border-amber-500/60 focus:ring-1 focus:ring-amber-500/30 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPass((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-400 transition-colors"
                  tabIndex={-1}
                >
                  {showPass ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
              {password.length > 0 && (
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-1 bg-zinc-800 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${strengthColor}`} style={{ width: `${(strength / 3) * 100}%` }} />
                  </div>
                  <span className="text-xs text-zinc-500">{strengthLabel}</span>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="confirm" className="block text-xs font-semibold text-zinc-400 uppercase tracking-widest mb-2">
                Repetir contraseña
              </label>
              <input
                id="confirm"
                type={showPass ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                required
                placeholder="Repita la contraseña"
                className={`w-full bg-zinc-900 border text-white placeholder-zinc-700 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-1 transition-all ${
                  confirm && confirm !== password
                    ? "border-red-500/50 focus:border-red-500/60 focus:ring-red-500/20"
                    : "border-zinc-800 focus:border-amber-500/60 focus:ring-amber-500/30"
                }`}
              />
              {confirm && confirm !== password && (
                <p className="text-red-400 text-xs mt-1.5">Las contraseñas no coinciden</p>
              )}
            </div>

            {error && (
              <div className="flex items-center gap-2.5 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3">
                <AlertIcon />
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || (!!confirm && confirm !== password)}
              className="w-full bg-amber-500 hover:bg-amber-400 active:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed text-zinc-950 font-bold py-3.5 rounded-lg text-sm tracking-wide transition-all mt-2 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <SpinnerIcon />
                  Creando cuenta...
                </>
              ) : (
                "Crear cuenta"
              )}
            </button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-zinc-600 text-sm">
              ¿Ya tiene cuenta?{" "}
              <Link href="/login" className="text-amber-500 hover:text-amber-400 font-medium transition-colors">
                Iniciar sesión
              </Link>
            </p>
          </div>

          <p className="mt-10 text-center text-zinc-700 text-xs">
            Al registrarse, acepta las condiciones de uso del sistema
          </p>
        </div>
      </div>
    </div>
  );
}

function ScalesIcon({ size = "lg" }: { size?: "sm" | "lg" }) {
  const d = size === "lg" ? 72 : 48;
  return (
    <svg width={d} height={d} viewBox="0 0 72 72" fill="none">
      <rect x="31" y="56" width="10" height="3" rx="1.5" fill="#d97706" opacity="0.8"/>
      <rect x="28" y="59" width="16" height="2.5" rx="1.25" fill="#d97706" opacity="0.6"/>
      <rect x="35" y="14" width="2" height="42" rx="1" fill="#d97706" opacity="0.7"/>
      <rect x="12" y="22" width="48" height="2" rx="1" fill="#d97706"/>
      <line x1="20" y1="24" x2="20" y2="32" stroke="#d97706" strokeWidth="1.5" strokeDasharray="2 2"/>
      <line x1="52" y1="24" x2="52" y2="32" stroke="#d97706" strokeWidth="1.5" strokeDasharray="2 2"/>
      <path d="M11 32 Q20 38 29 32" stroke="#d97706" strokeWidth="2" fill="none" strokeLinecap="round"/>
      <path d="M43 32 Q52 38 61 32" stroke="#d97706" strokeWidth="2" fill="none" strokeLinecap="round"/>
      <circle cx="12" cy="23" r="2" fill="#d97706"/>
      <circle cx="60" cy="23" r="2" fill="#d97706"/>
      <circle cx="36" cy="15" r="3" fill="#d97706"/>
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg className="w-3.5 h-3.5 text-amber-500 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}
function EyeIcon() {
  return <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;
}
function EyeOffIcon() {
  return <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>;
}
function AlertIcon() {
  return <svg className="w-4 h-4 text-red-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>;
}
function SpinnerIcon() {
  return <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>;
}
